import { useState } from 'react';
import { Badge, Button, Checkbox, Field, Input, Panel, PanelBody, PanelHeader, SectionLabel } from '../../ui/index.js';
import { getWooOrderStatus } from '@mapara/core';
import {
  ACTION_LABEL,
  CONFIRMATION_LABEL,
  RISK_LABEL,
  RISK_TONE,
  lockLabel,
  money,
  relative,
  shortDate,
  STATUS_DOT,
} from './presentation.js';
import { OrderEditor, type EditorPatch, type PreviewResult } from './OrderEditor.js';
import type { AttemptOutcome, ShopOrderDetail } from './types.js';

export interface InspectorActions {
  readonly busy: string | null;
  readonly flash: string | null;
  readonly editing: boolean;
  readonly savingEdit: boolean;
  readonly editError: string | null;
  readonly editWarnings: readonly string[];
  readonly preview: PreviewResult | null;
  readonly onAttempt: (outcome: AttemptOutcome, notes: string, callbackAt: string) => void;
  readonly onConfirm: () => void;
  readonly onCancelOrder: (reason: string) => void;
  readonly onParcel: () => void;
  readonly onAssign: () => void;
  readonly onToggleLock: () => void;
  readonly onNotes: (payload: { internalNotes: string; deliveryNotes: string; tel2: string; fragile: boolean; ouvrir: boolean }) => void;
  readonly onToggleEdit: () => void;
  readonly onPreviewEdit: (patch: EditorPatch) => void;
  readonly onSaveEdit: (patch: EditorPatch) => void;
}

export interface OrderInspectorProps {
  readonly detail: ShopOrderDetail;
  readonly actions: InspectorActions;
}

const OUTCOMES: readonly { value: AttemptOutcome; label: string; key: string; color: string }[] = [
  { value: 'no_answer', label: 'Pas de réponse', key: '1', color: 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100' },
  { value: 'unreachable', label: 'Injoignable', key: '2', color: 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100' },
  { value: 'callback', label: 'Rappel', key: '3', color: 'border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100' },
  { value: 'confirmed', label: 'Confirmée', key: '4', color: 'border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100' },
  { value: 'cancelled', label: 'Annulée', key: '5', color: 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100' },
];

function StatusBadge({ status }: { readonly status: string }): React.ReactElement {
  const dot = STATUS_DOT[status] ?? 'bg-gray-400';
  const label = CONFIRMATION_LABEL[status] ?? status;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border border-border bg-panel px-3 py-1 text-xs font-semibold`}>
      <span className={`h-2 w-2 rounded-full ${dot}`} />
      {label}
    </span>
  );
}

function InfoRow({ label, value, mono }: { readonly label: string; readonly value: string; readonly mono?: boolean }): React.ReactElement {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-ink-faint">{label}</span>
      <span className={`text-sm font-medium text-ink ${mono ? 'font-mono tabular-nums' : ''}`}>{value}</span>
    </div>
  );
}

export function OrderInspector({ detail, actions }: OrderInspectorProps): React.ReactElement {
  const order = detail.order;
  const [outcome, setOutcome] = useState<AttemptOutcome>('no_answer');
  const [notes, setNotes] = useState('');
  const [callbackAt, setCallbackAt] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [tel2, setTel2] = useState(order.tel2 ?? '');
  const [fragile, setFragile] = useState(order.fragile);
  const [ouvrir, setOuvrir] = useState(order.ouvrir);
  const [internalNotes, setInternalNotes] = useState(order.internalNotes ?? '');
  const [deliveryNotes, setDeliveryNotes] = useState(order.deliveryNotes ?? '');

  const woo = getWooOrderStatus(order.status);
  const blocked = order.locked && !order.lockedByMe;
  const busy = actions.busy !== null;

  return (
    <aside className="flex w-[28rem] shrink-0 flex-col gap-3 overflow-y-auto border-l border-border bg-gradient-to-b from-panel/40 to-transparent px-4 py-4">
      <header className="flex flex-col gap-2.5 rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-xs tabular-nums text-ink-faint">#{order.number}</p>
            <h2 className="text-lg font-bold leading-tight text-ink">{order.customerName ?? 'Client inconnu'}</h2>
          </div>
          <div className="flex flex-col items-end gap-1">
            <StatusBadge status={order.confirmationStatus} />
            <Badge tone={RISK_TONE[detail.riskLevel] ?? 'neutral'}>
              <span className={`mr-1 inline-block h-2 w-2 rounded-full ${STATUS_DOT[detail.riskLevel] ?? 'bg-gray-400'}`} />
              {RISK_LABEL[detail.riskLevel] ?? detail.riskLevel}
            </Badge>
          </div>
        </div>
        <p className="text-xs text-ink-faint">
          {woo?.badgeLabel ?? order.status} · {money(order.total)} · {relative(order.dateCreated)}
        </p>
        {order.locked && (
          <p className="rounded-lg bg-clay-600/10 px-2.5 py-1.5 text-[11px] font-medium text-clay-700">
            🔒 {lockLabel(order.lockedBy, order.lockExpiresAt)}
          </p>
        )}
        {actions.flash !== null && (
          <p className="rounded-lg bg-herb-600/10 px-2.5 py-1.5 text-[11px] font-medium text-herb-700">{actions.flash}</p>
        )}
      </header>

      <div className="flex flex-wrap gap-1.5">
        <Button size="sm" variant="ghost" disabled={busy} onClick={actions.onAssign}>
          {order.assignedTo === null ? '👤 Prendre' : '👤 Réaffecter'}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={actions.onToggleLock}>
          {order.locked ? '🔓 Déverrouiller' : '🔒 Verrouiller'}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={actions.onToggleEdit}>
          {actions.editing ? '✕ Fermer' : '✎ Modifier'}
        </Button>
        <a href={`/shop-manager/${order.wooId}/picking`} target="_blank" rel="noreferrer">
          <Button size="sm" variant="ghost">🧾 Préparation</Button>
        </a>
        <a href={`/shop-manager/${order.wooId}/thermal`} target="_blank" rel="noreferrer">
          <Button size="sm" variant="ghost">🖨 Étiquette</Button>
        </a>
      </div>

      {actions.editing ? (
        <OrderEditor
          detail={detail}
          saving={actions.savingEdit}
          error={actions.editError}
          warnings={actions.editWarnings}
          preview={actions.preview}
          onPreview={actions.onPreviewEdit}
          onSave={actions.onSaveEdit}
          onCancel={actions.onToggleEdit}
        />
      ) : (
        <Panel>
          <PanelHeader index="☎" title="Appel" hint="1–5 puis Entrée" />
          <PanelBody className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-1.5">
              {OUTCOMES.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  disabled={blocked || busy}
                  onClick={() => setOutcome(item.value)}
                  className={[
                    'flex items-center justify-between rounded-lg border px-3 py-2.5 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-40',
                    outcome === item.value
                      ? 'border-herb-600 bg-herb-600/10 text-herb-700 ring-1 ring-herb-600/30'
                      : item.color,
                  ].join(' ')}
                >
                  {item.label}
                  <kbd className="rounded border border-border bg-panel px-1.5 py-0.5 text-[10px] text-ink-faint">{item.key}</kbd>
                </button>
              ))}
            </div>

            {outcome === 'callback' && (
              <Field label="Heure du rappel" htmlFor="sm-callback" required>
                <Input
                  id="sm-callback"
                  type="datetime-local"
                  value={callbackAt}
                  onChange={(event) => setCallbackAt(event.target.value)}
                />
              </Field>
            )}
            {outcome === 'cancelled' && (
              <Field label="Motif d'annulation" htmlFor="sm-reason" required error={cancelReason.trim() === ''}>
                <Input
                  id="sm-reason"
                  value={cancelReason}
                  placeholder="Client injoignable, erreur de quantité..."
                  onChange={(event) => setCancelReason(event.target.value)}
                />
              </Field>
            )}

            <Field label="Note d'appel" htmlFor="sm-attempt-notes">
              <textarea
                id="sm-attempt-notes"
                rows={2}
                value={notes}
                maxLength={1000}
                onChange={(event) => setNotes(event.target.value)}
                className="w-full rounded-lg border border-border bg-card px-3 py-2 text-sm text-ink outline-none focus:border-herb-600 focus:ring-2 focus:ring-herb-600/15"
              />
            </Field>
            <Button
              variant="primary"
              size="md"
              disabled={blocked || busy || (outcome === 'callback' && callbackAt === '') || (outcome === 'cancelled' && cancelReason.trim() === '')}
              onClick={() => actions.onAttempt(outcome, notes, callbackAt)}
            >
              Enregistrer l&apos;appel
            </Button>
          </PanelBody>
        </Panel>
      )}

      <Panel>
        <PanelHeader index="✓" title="Décision" />
        <PanelBody className="flex flex-col gap-2">
          <div className="flex flex-wrap gap-1.5">
            <Button
              size="sm"
              variant="primary"
              disabled={busy || order.confirmedAt !== null}
              onClick={actions.onConfirm}
            >
              {order.confirmedAt !== null ? '✓ Déjà confirmée' : '✓ Confirmer'}
            </Button>
            <Button size="sm" disabled={busy || order.jetpackCode !== null} onClick={actions.onParcel}>
              {order.jetpackCode === null ? '📦 Créer le colis' : `📦 ${order.jetpackCode}`}
            </Button>
            {detail.whatsapp.confirmation !== null && (
              <a href={detail.whatsapp.confirmation} target="_blank" rel="noreferrer">
                <Button size="sm" variant="ghost">WhatsApp</Button>
              </a>
            )}
          </div>
          {order.cancelReason !== null && (
            <p className="text-[11px] text-ink-faint">Annulée : {order.cancelReason}</p>
          )}
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader index="📦" title="Articles" hint={`${order.lineItems.length} ligne(s)`} />
        <PanelBody>
          <ul className="flex flex-col gap-1">
            {order.lineItems.map((item, index) => (
              <li key={`${item.sku ?? item.name ?? index}-${index}`} className="flex items-center justify-between rounded-lg bg-panel/50 px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{item.name ?? 'Article'}</p>
                  <p className="font-mono text-[10px] text-ink-faint">{item.sku ?? '—'}</p>
                </div>
                <div className="ml-3 flex items-center gap-3 text-sm">
                  <span className="tabular-nums text-ink-faint">×{item.qty}</span>
                  <span className="tabular-nums font-medium text-ink">{money(item.price)}</span>
                </div>
              </li>
            ))}
            {order.lineItems.length === 0 && <li className="text-xs text-ink-faint">Aucune ligne.</li>}
          </ul>
          <div className="mt-2 flex items-center justify-between border-t border-border pt-2">
            <span className="text-xs font-semibold text-ink-faint">Total TTC</span>
            <span className="text-sm font-bold tabular-nums text-ink">{money(order.total)}</span>
          </div>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader index="👤" title="Client" />
        <PanelBody>
          <div className="flex items-center gap-3 rounded-lg bg-panel/50 p-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-herb-600/10 text-lg font-bold text-herb-700">
              {(order.customerName ?? '?').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-ink">{order.customerName ?? '—'}</p>
              <p className="font-mono text-xs tabular-nums text-ink-faint">{order.billingPhone ?? '—'}</p>
              {order.billingEmail && (
                <p className="truncate font-mono text-xs text-ink-faint">{order.billingEmail}</p>
              )}
            </div>
          </div>
          <div className="mt-2 divide-y divide-border/50">
            {order.billingEmail && <InfoRow label="Email" value={order.billingEmail} />}
            <InfoRow label="Adresse" value={order.billingAddress ?? '—'} />
            <InfoRow label="Ville" value={order.billingCity ?? '—'} />
            <InfoRow label="Gouvernorat" value={order.billingGov ?? '—'} />
            {order.carrierName && <InfoRow label="Transporteur" value={order.carrierName} />}
            {order.trackingCode && <InfoRow label="N° Suivi" value={order.trackingCode} />}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-panel/50 p-2.5 text-center">
              <p className="text-lg font-bold tabular-nums text-ink">{detail.stats.totalParcels}</p>
              <p className="text-[10px] text-ink-faint">Colis</p>
            </div>
            <div className="rounded-lg bg-panel/50 p-2.5 text-center">
              <p className="text-lg font-bold tabular-nums text-ink">{detail.stats.deliveredCount}</p>
              <p className="text-[10px] text-ink-faint">Livrés</p>
            </div>
            <div className="rounded-lg bg-panel/50 p-2.5 text-center">
              <p className="text-lg font-bold tabular-nums text-ink">{detail.stats.returnedCount}</p>
              <p className="text-[10px] text-ink-faint">Retournés</p>
            </div>
            <div className="rounded-lg bg-panel/50 p-2.5 text-center">
              <p className="text-lg font-bold tabular-nums text-ink">{Math.round(detail.stats.returnRate * 100)}%</p>
              <p className="text-[10px] text-ink-faint">Taux retour</p>
            </div>
          </div>
          <div className="mt-2 rounded-lg bg-panel/50 p-2.5">
            <div className="flex items-center justify-between text-xs">
              <span className="text-ink-faint">CA livré</span>
              <span className="font-semibold tabular-nums text-ink">{money(detail.stats.ltv)}</span>
            </div>
          </div>
          {detail.duplicates.length > 0 && (
            <div className="mt-3">
              <SectionLabel>Doublons (48 h)</SectionLabel>
              <ul className="flex flex-col gap-0.5">
                {detail.duplicates.map((dup) => (
                  <li key={dup.id} className="flex items-center justify-between rounded bg-red-50 px-2 py-1 text-[11px]">
                    <span className="font-mono text-red-700">#{dup.number}</span>
                    <span className="tabular-nums text-red-600">{shortDate(dup.dateCreated)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader index="📝" title="Options COD" hint="Reprises par Jetpack" />
        <PanelBody className="flex flex-col gap-3">
          <Field label="Téléphone 2" htmlFor="sm-tel2">
            <Input id="sm-tel2" value={tel2} onChange={(event) => setTel2(event.target.value)} className="tabular-nums" />
          </Field>
          <div className="flex gap-4">
            <Checkbox checked={fragile} onChange={(event) => setFragile(event.target.checked)} label="Fragile" />
            <Checkbox checked={ouvrir} onChange={(event) => setOuvrir(event.target.checked)} label="Ouvrir le colis" />
          </div>
          <Field label="Note de livraison" htmlFor="sm-delivery">
            <Input id="sm-delivery" value={deliveryNotes} onChange={(event) => setDeliveryNotes(event.target.value)} />
          </Field>
          <Field label="Note interne" htmlFor="sm-internal">
            <Input id="sm-internal" value={internalNotes} onChange={(event) => setInternalNotes(event.target.value)} />
          </Field>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={() => actions.onNotes({ internalNotes, deliveryNotes, tel2, fragile, ouvrir })}
          >
            Enregistrer les options
          </Button>
        </PanelBody>
      </Panel>

      <Panel>
        <PanelHeader index="🕘" title="Journal" hint={`${detail.activities.length} entrées`} />
        <PanelBody>
          <ol className="flex flex-col gap-2">
            {detail.activities.map((activity) => (
              <li key={activity.id} className="border-l-2 border-border pl-3 text-xs">
                <p className="font-medium text-ink">
                  {ACTION_LABEL[activity.action] ?? activity.action}
                  {activity.outcome !== null && <span className="text-ink-faint"> · {activity.outcome}</span>}
                </p>
                {activity.notes !== null && <p className="mt-0.5 text-ink-faint">{activity.notes}</p>}
                <p className="mt-0.5 text-[10px] text-ink-faint">
                  {activity.operatorName} · {relative(activity.createdAt)}
                </p>
              </li>
            ))}
            {detail.activities.length === 0 && <li className="text-xs text-ink-faint">Aucune action.</li>}
          </ol>
        </PanelBody>
      </Panel>
    </aside>
  );
}